// When answers actually arrived.
//
// A survey sent on Monday collects replies for days. "67 of 100 completed"
// says nothing about whether they came in one burst or trickled in over a
// week, which is what tells an author whether a reminder is worth sending or
// the field period was long enough.
//
// The timestamp used is the LAST response of a take, not the first: a take
// belongs to the moment the person finished, not the moment they opened it.
// The adapted response carries the start (`response_date`) and how long the
// take ran (`completion_time`, seconds), so the finish is derived from the
// two rather than needing a new field from the service.
//
// UI-FREE: no React. Safe to unit-test in isolation.

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** The moment a take finished, or null when it cannot be established. */
export function lastResponseAt(response) {
  const started = response?.response_date ? new Date(response.response_date) : null;
  if (!started || Number.isNaN(started.getTime())) return null;
  const seconds = Number(response?.completion_time);
  const elapsed = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
  return new Date(started.getTime() + elapsed);
}

const startOfHour = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours());
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const hourLabel = (d) => d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric' });
const dayLabel = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/**
 * @returns {{
 *   granularity: 'hour'|'day'|'none',
 *   buckets: Array<{ key: number, label: string, count: number, cumulative: number }>,
 *   total: number, first: Date|null, last: Date|null, spanHours: number
 * }}
 */
export function buildResponseTiming(responses = []) {
  const stamps = (Array.isArray(responses) ? responses : [])
    .map(lastResponseAt)
    .filter(Boolean)
    .sort((a, b) => a - b);

  if (!stamps.length) {
    return { granularity: 'none', buckets: [], total: 0, first: null, last: null, spanHours: 0 };
  }

  const first = stamps[0];
  const last = stamps[stamps.length - 1];
  const spanMs = last - first;
  // Granularity is chosen by how many columns it would PRODUCE, not by a span
  // threshold. A "two days or less" rule still drew 49 hourly columns for a
  // span of exactly two days, which is the unreadable chart it was meant to
  // prevent. Hours are used only while they stay countable.
  const MAX_READABLE_BUCKETS = 48;
  const hourBuckets = Math.floor(spanMs / HOUR_MS) + 1;
  const granularity = hourBuckets <= MAX_READABLE_BUCKETS ? 'hour' : 'day';
  const floor = granularity === 'hour' ? startOfHour : startOfDay;
  const label = granularity === 'hour' ? hourLabel : dayLabel;
  const step = granularity === 'hour' ? HOUR_MS : DAY_MS;

  const counts = new Map();
  stamps.forEach(stamp => {
    const key = floor(stamp).getTime();
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  // Every interval between the first and last answer appears, including the
  // empty ones. A gap is the point: a day with no replies is a fact about the
  // collection, and dropping it would draw a steady trickle that never was.
  const buckets = [];
  let cumulative = 0;
  for (let key = floor(first).getTime(); key <= floor(last).getTime(); key += step) {
    const count = counts.get(key) || 0;
    cumulative += count;
    buckets.push({ key, label: label(new Date(key)), count, cumulative });
    if (buckets.length > 400) break; // a runaway span is not a chart
  }

  return {
    granularity,
    buckets,
    total: stamps.length,
    first,
    last,
    spanHours: Math.round(spanMs / HOUR_MS),
    spanMs,
  };
}

/** Plain-language summary of how long collection actually took. */
export function describeCollection(timing) {
  if (!timing || timing.granularity === 'none') return 'No completed answers to time yet.';
  const { total, spanMs } = timing;
  // Decided on the exact span, not a rounded hour count: half an hour rounds
  // to 1 and used to read "over 1 hours".
  if (spanMs < HOUR_MS) return `All ${total} answers arrived within the hour.`;
  if (spanMs < 2 * DAY_MS) {
    const hours = Math.round(spanMs / HOUR_MS);
    return `${total} answers arrived over ${hours} ${hours === 1 ? 'hour' : 'hours'}.`;
  }
  const days = Math.round(spanMs / DAY_MS);
  return `${total} answers arrived over ${days} ${days === 1 ? 'day' : 'days'}.`;
}
